import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, Monitor, Wrench, Phone, Mail, MapPin, Star, Shield, Clock, Zap, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const Index = () => {
  useEffect(() => {
    // Handle smooth scrolling for anchor links
    const handleAnchorClick = (e: Event) => {
      const target = e.target as HTMLAnchorElement;
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const id = target.getAttribute('href')?.substring(1);
        const element = document.getElementById(id || '');
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    systemType: 'Residential Solar System',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.message) {
      alert('Please fill in all required fields.');
      return;
    }

    // Create mailto link with form data
    const subject = `Solar Monitoring Inquiry from ${formData.firstName} ${formData.lastName}`;
    const body = `Name: ${formData.firstName} ${formData.lastName}\nEmail: ${formData.email}\nPhone: ${formData.phone || 'Not provided'}\nSystem Type: ${formData.systemType}\n\nMessage:\n${formData.message}`;
    
    const mailtoLink = `mailto:solar.care.monitoring@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Show success message
    setSubmitStatus('success');
    
    // Clear form
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      systemType: 'Residential Solar System',
      message: ''
    });
  };

  // Test Supabase connection
  const testConnection = async () => {
    try {
      console.log('Testing Supabase connection...');
      const { data, error } = await supabase.functions.invoke('send_contact_email_fixed_2026_01_14_19_00', {
        body: {
          firstName: 'Test',
          lastName: 'User',
          email: 'test@example.com',
          phone: '555-0123',
          systemType: 'Residential Solar System',
          message: 'This is a test message to verify the contact form is working.'
        }
      });
      console.log('Test result:', { data, error });
    } catch (error) {
      console.error('Test failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <h1 className="text-2xl font-bold text-primary">Solar Care Monitoring</h1>
          </div>
          <nav className="hidden md:flex items-center space-x-6">
            <a href="#services" className="text-muted-foreground hover:text-primary smooth-transition">Services</a>
            <a href="#pricing" className="text-muted-foreground hover:text-primary smooth-transition">Pricing</a>
            <a href="#contact" className="text-muted-foreground hover:text-primary smooth-transition">Contact</a>
            <Button asChild className="solar-gradient text-white hover:opacity-90 smooth-transition">
              <a href="#contact">Get Quote</a>
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="sky-gradient py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Professional Solar System
              <span className="block text-primary">Monitoring & Service</span>
            </h2>
            <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
              $99 home inspections, old system upgrades and maintenance, remote monitoring, and professional troubleshooting for residential and commercial solar installations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="solar-gradient text-white hover:opacity-90 smooth-transition solar-glow">
                <a href="#contact">Start Monitoring Today</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white smooth-transition">
                <a href="#contact">Schedule Service</a>
              </Button>
            </div>
          </div>
          <div className="mt-16">
            <img 
              src="/images/las_vegas_solar_installation_2.png" 
              alt="Solar panel installation team working on residential home in Las Vegas Nevada" 
              className="rounded-lg shadow-2xl mx-auto max-w-4xl w-full"
            />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive solar system care from home inspections to cutting-edge remote monitoring and maintenance.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Service 1 */}
            <Card className="hover:shadow-lg smooth-transition border-l-4 border-l-primary">
              <CardHeader>
                <div className="w-12 h-12 solar-gradient rounded-lg flex items-center justify-center mb-4">
                  <Wrench className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-2xl">System Service</CardTitle>
                <CardDescription className="text-base">
                  Expert maintenance and repair for solar installations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Complete system diagnostics</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Component replacement & upgrades</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Performance optimization</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Preventive maintenance plans</span>
                  </li>
                </ul>
                <img 
                  src="/images/technician_service_1.jpeg" 
                  alt="Solar technician performing maintenance" 
                  className="rounded-lg mt-4 w-full h-48 object-cover"
                />
              </CardContent>
            </Card>

            {/* Service 2 */}
            <Card className="hover:shadow-lg smooth-transition border-l-4 border-l-accent">
              <CardHeader>
                <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mb-4">
                  <Monitor className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-2xl">Remote Monitoring</CardTitle>
                <CardDescription className="text-base">
                  24/7 inverter monitoring with monthly subscription plans
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Real-time performance tracking</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Automated alert system</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Monthly performance reports</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Mobile app access</span>
                  </li>
                </ul>
                <img 
                  src="/images/monitoring_dashboard_1.png" 
                  alt="Solar monitoring dashboard" 
                  className="rounded-lg mt-4 w-full h-48 object-cover"
                />
              </CardContent>
            </Card>

            {/* Service 3 */}
            <Card className="hover:shadow-lg smooth-transition border-l-4 border-l-blue-500">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-2xl">Remote Troubleshooting</CardTitle>
                <CardDescription className="text-base">
                  Expert diagnosis and problem resolution from anywhere
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Instant issue identification</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Remote system adjustments</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Priority support access</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Detailed resolution reports</span>
                  </li>
                </ul>
                <img 
                  src="/images/monitoring_dashboard_2.jpeg" 
                  alt="Solar system troubleshooting interface" 
                  className="rounded-lg mt-4 w-full h-48 object-cover"
                />
              </CardContent>
            </Card>

            {/* Service 4 - Home Inspection */}
            <Card className="hover:shadow-lg smooth-transition border-l-4 border-l-purple-500">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center mb-4">
                  <Search className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-2xl">Home Inspection</CardTitle>
                <CardDescription className="text-base">
                  Comprehensive on-site solar system evaluation for just $99
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Complete system assessment</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Performance analysis report</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Safety & compliance check</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-accent" />
                    <span>Maintenance recommendations</span>
                  </li>
                </ul>
                <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600">$99</div>
                    <div className="text-sm text-purple-600">One-time fee</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 sky-gradient">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Why Choose SolarCare Monitoring?</h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Trusted by hundreds of residential and commercial solar system owners.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 solar-gradient rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold mb-2">24/7 Monitoring</h4>
              <p className="text-gray-600">Round-the-clock system surveillance and instant alerts</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold mb-2">Expert Technicians</h4>
              <p className="text-gray-600">Certified professionals with years of solar experience</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold mb-2">Guaranteed Results</h4>
              <p className="text-gray-600">Satisfaction guaranteed or your money back</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold mb-2">Fast Response</h4>
              <p className="text-gray-600">Quick diagnosis and resolution of system issues</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Service Comparison Table</h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Compare our service plans and choose what works best for your solar system.
            </p>
          </div>
          
          {/* Service Plans Header */}
          <div className="max-w-6xl mx-auto mb-8">
            <div className="grid grid-cols-4 gap-4 mb-8">
              <div></div>
              <Card className="text-center p-6">
                <CardTitle className="text-2xl mb-2">Basic Service</CardTitle>

                <p className="text-sm text-gray-600">Perfect for residential systems</p>
              </Card>
              <Card className="text-center p-6 border-2 border-primary relative">
                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 solar-gradient text-white">
                  Most Popular
                </Badge>
                <CardTitle className="text-2xl mb-2">Professional Service</CardTitle>

                <p className="text-sm text-gray-600">Ideal for commercial systems</p>
              </Card>
              <Card className="text-center p-6">
                <CardTitle className="text-2xl mb-2">Enterprise Service</CardTitle>

                <p className="text-sm text-gray-600">For large installations</p>
              </Card>
            </div>
            
            {/* Features Table */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="grid grid-cols-4 gap-0">
                {/* Feature Names Column */}
                <div className="bg-gray-50">
                  <div className="p-4 font-semibold text-gray-900 border-b">Features</div>
                  <div className="p-4 border-b text-sm pt-[18px] pb-[18px]">Real-Time Monitoring</div>
                  <div className="p-4 border-b text-sm pt-[18px] pb-[18px]">Remote Troubleshooting</div>
                  <div className="p-4 border-b text-sm pt-[18px] pb-[18px]">Email Alerts</div>
                  <div className="p-4 border-b text-sm pt-[18px] pb-[18px]">Free Power Bill Analysis</div>
                  <div className="p-4 border-b text-sm pt-[18px] pb-[18px]">Discounted Labor</div>

                  <div className="p-4 border-b text-sm pt-[18px] pb-[18px]">Priority Support</div>
                  <div className="p-4 text-sm pt-[18px] pb-[18px]">Yearly Panel Cleaning</div>
                </div>
                
                {/* Basic Service Column */}
                <div className="border-l">
                  <div className="p-4 font-semibold text-center border-b bg-blue-50">Basic Service</div>
                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>

                  <div className="p-4 border-b text-center">
                    <X className="w-6 h-6 text-red-400 mx-auto" />
                  </div>
                  <div className="p-4 text-center">
                    <X className="w-6 h-6 text-red-400 mx-auto" />
                  </div>
                </div>
                
                {/* Professional Service Column */}
                <div className="border-l bg-blue-50">
                  <div className="p-4 font-semibold text-center border-b bg-blue-100">Professional Service</div>
                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>

                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                  <div className="p-4 text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                </div>
                
                {/* Enterprise Service Column */}
                <div className="border-l">
                  <div className="p-4 font-semibold text-center border-b bg-purple-50">Enterprise Service</div>
                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                  <div className="p-4 border-b text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>

                  <div className="p-4 text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                  </div>
                </div>
              </div>
            </div>
            

          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 sky-gradient">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Get Started Today</h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Ready to optimize your solar system performance? Contact us for a free consultation.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {/* Contact Info */}
            <div>
              <h4 className="text-2xl font-semibold mb-6">Contact Information</h4>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 solar-gradient rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold">Phone</p>
                    <p className="text-gray-600">702-420-8901</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold">Email</p>
                    <p className="text-gray-600">solar.care.monitoring@gmail.com</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold">Service Area</p>
                    <p className="text-gray-600">Southern Nevada</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <img 
                  src="/images/technician_service_2.jpeg" 
                  alt="Professional solar service team" 
                  className="rounded-lg w-full h-64 object-cover"
                />
              </div>
            </div>
            
            {/* Contact Form */}
            <Card>
              <CardHeader>
                <CardTitle>Request an Inspection</CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you within 24 hours.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">First Name</label>
                      <input 
                        type="text" 
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="John"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Last Name</label>
                      <input 
                        type="text" 
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Doe"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone</label>
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">System Type</label>
                    <select 
                      name="systemType"
                      value={formData.systemType}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="Residential Solar System">Residential Solar System</option>
                      <option value="Commercial Solar System">Commercial Solar System</option>
                      <option value="Industrial Solar System">Industrial Solar System</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Message</label>
                    <textarea 
                      rows={4}
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Tell us about your solar system and monitoring needs..."
                      required
                    ></textarea>
                  </div>
                  
                  {submitStatus === 'success' && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-md text-green-800">
                      ✅ Email client opened! Please send the pre-filled email to complete your inquiry.
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-800">
                      ❌ Failed to send message. Please check the browser console for details or contact us directly:
                      <br />📞 Call: <a href="tel:702-420-8901" className="underline">702-420-8901</a>
                      <br />📧 Email: <a href={`mailto:solar.care.monitoring@gmail.com?subject=Solar Monitoring Inquiry from ${formData.firstName} ${formData.lastName}&body=Name: ${formData.firstName} ${formData.lastName}%0AEmail: ${formData.email}%0APhone: ${formData.phone}%0ASystem Type: ${formData.systemType}%0A%0AMessage:%0A${formData.message}`} className="underline">solar.care.monitoring@gmail.com</a>
                    </div>
                  )}
                  
                  <Button 
                    type="submit" 
                    className="w-full solar-gradient text-white hover:opacity-90"
                  >
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 solar-gradient rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold">SolarCare Monitoring</h3>
              </div>
              <p className="text-gray-400">
                Professional solar system monitoring and maintenance services for residential and commercial installations.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Home Inspections ($99)</li>
                <li>System Service</li>
                <li>Remote Monitoring</li>
                <li>Remote Troubleshooting</li>
                <li>System Optimization</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li>About Us</li>
                <li>Our Team</li>
                <li>Careers</li>
                <li>Contact</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Help Center</li>
                <li>Documentation</li>
                <li>System Status</li>
                <li>Emergency Support</li>
              </ul>
            </div>
          </div>
          
          <Separator className="my-8 bg-gray-700" />
          
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400">
              <p>© 2024 Solar Care Monitoring. All rights reserved.</p>
              <p className="mt-1">Contact us: <a href="mailto:solar.care.monitoring@gmail.com" className="text-primary hover:text-white transition-colors">solar.care.monitoring@gmail.com</a></p>
            </div>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white smooth-transition">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-white smooth-transition">Terms of Service</a>
              <a href="#" className="text-gray-400 hover:text-white smooth-transition">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;